import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  getDragCustomerName,
  getSelectedAssignPayload,
  subscribeSelectedAssign,
} from '@/pages/sale/lib/sale-comment-drag';
import { FacebookComment } from '@/types/fb-type';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CommentBubble } from '../CommentBuble';

type LiveCommentsListProps = {
  comments: FacebookComment[];
  filteredComments: FacebookComment[];
  isLoading: boolean;
  showNewBanner: boolean;
  onPinToggle?: (id: string, pinned: boolean) => void;
  compact?: boolean;
  dense?: boolean;
};

export function LiveCommentsList({
  comments,
  filteredComments,
  isLoading,
  showNewBanner,
  onPinToggle,
  compact = false,
  dense = false,
}: LiveCommentsListProps) {
  return (
    <ScrollArea className="min-h-0 min-w-0 flex-1 [&>[data-radix-scroll-area-viewport]>div]:!block [&>[data-radix-scroll-area-viewport]>div]:max-w-full">
      <div
        className={cn(
          'box-border min-w-0 max-w-full',
          dense
            ? 'space-y-1.5 p-1'
            : compact
              ? 'space-y-2 p-1.5 sm:space-y-2.5 sm:p-2'
              : 'space-y-3 p-2.5 sm:p-3',
        )}
      >
        {showNewBanner && <LiveCommentsNewBanner />}
        <LiveCommentsAssignBanner />

        {isLoading && comments.length === 0 ? (
          <LiveCommentsLoadingSkeleton compact={compact} />
        ) : filteredComments.length === 0 ? (
          <LiveCommentsEmptyState />
        ) : (
          filteredComments.map((comment) => (
            <CommentBubble
              key={comment.id}
              data={comment}
              onPinToggle={onPinToggle}
              compact={compact}
              dense={dense}
            />
          ))
        )}
      </div>
    </ScrollArea>
  );
}

function LiveCommentsNewBanner() {
  return (
    <div className="mb-1 inline-flex w-full items-center justify-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-center text-[10px] text-blue-700">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
      <span>New comments</span>
    </div>
  );
}

function LiveCommentsAssignBanner() {
  const [selectedName, setSelectedName] = useState<string | null>(null);

  useEffect(() => {
    const sync = () => {
      const selected = getSelectedAssignPayload();
      setSelectedName(selected ? getDragCustomerName(selected) : null);
    };
    sync();
    return subscribeSelectedAssign(sync);
  }, []);

  if (!selectedName) return null;

  return (
    <div className="mb-1 inline-flex w-full items-center justify-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-center text-[10px] font-medium text-indigo-700">
      <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
      <span>Comment selected — tap a slot to assign {selectedName}</span>
    </div>
  );
}

function LiveCommentsLoadingSkeleton({ compact }: { compact?: boolean }) {
  return (
    <div className={compact ? 'animate-pulse space-y-2 p-1 sm:space-y-3 sm:p-2' : 'animate-pulse space-y-3 p-3'}>
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-start gap-2 sm:gap-3">
          <div className="h-8 w-8 shrink-0 rounded-full bg-slate-200 sm:h-10 sm:w-10" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-1/3 rounded bg-slate-200 sm:h-4" />
            <div className="h-3 w-2/3 rounded bg-slate-200" />
            <div className="h-3 w-1/2 rounded bg-slate-200" />
          </div>
        </div>
      ))}
    </div>
  );
}

function LiveCommentsEmptyState() {
  return (
    <div className="py-12 text-center text-sm text-gray-400 sm:py-16">
      No comments found.
    </div>
  );
}

type LiveCommentsPanelFooterProps = {
  isLoading: boolean;
  hasComments: boolean;
};

export function LiveCommentsPanelFooter({
  isLoading,
  hasComments,
  dense,
}: LiveCommentsPanelFooterProps & { dense?: boolean }) {
  if (!isLoading || !hasComments) return null;

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center gap-1.5 border-t text-[11px] text-gray-500',
        dense ? 'py-1.5' : 'py-2',
      )}
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading comments…
    </div>
  );
}
